import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useConfig } from 'wagmi'

import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { getValidatorConfig as getDepositValidatorConfig } from '@/features/savings/dialogs/deposit/logic/useDepositToSavingsValidator'
import { getValidatorConfig as getWithdrawValidatorConfig } from '@/features/savings/dialogs/withdraw/logic/form/useWithdrawFromSavingsValidator'
import { SavingsAccountRepository } from '@/features/savings/logic/savings-converters/types'
import { useTimestamp } from '@/utils/useTimestamp'

export interface UsePrefetchValidatorsParams {
  chainId: number
  tokenRepository: TokenRepository
  savingsAccounts: SavingsAccountRepository
}

export function usePrefetchValidators({
  chainId,
  tokenRepository,
  savingsAccounts,
}: UsePrefetchValidatorsParams): void {
  const queryClient = useQueryClient()
  const wagmiConfig = useConfig()
  const { timestamp } = useTimestamp()

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    for (const savingsAccount of savingsAccounts.all()) {
      const depositValidatorConfig = getDepositValidatorConfig({
        chainId,
        tokenRepository,
        wagmiConfig,
        savingsAccount,
        timestamp,
      })
      void queryClient.prefetchQuery({
        queryKey: depositValidatorConfig.fetchParamsQueryKey,
        queryFn: depositValidatorConfig.fetchParamsQueryFn,
      })

      const withdrawValidatorConfig = getWithdrawValidatorConfig({
        chainId,
        timestamp,
        tokenRepository,
        wagmiConfig,
        savingsToken: savingsAccount.savingsToken,
        underlyingToken: savingsAccount.underlyingToken,
        savingsTokenBalance: tokenRepository.findOneBalanceOrZero(savingsAccount.savingsToken.symbol),
        savingsConverter: savingsAccount.converter,
      })

      void queryClient.prefetchQuery({
        queryKey: withdrawValidatorConfig.fetchParamsQueryKey,
        queryFn: withdrawValidatorConfig.fetchParamsQueryFn,
      })
    }
  }, [chainId, queryClient, wagmiConfig])
}
